// apps/client/src/pages/team/unfinishedSetupCopy.ts
//
// WS8b M5 (V6-03) + M6 (V6-02): the ONE source of the unfinished-setup wording
// and badge states, shared by the legacy Team page and the V2 Invitations tab.
//
// Both surfaces must say the same thing, because both drive the same callable
// and the same two idempotent presses. Duplicating the strings is how the two
// team-management surfaces drifted in the first place (the lever landed on the
// legacy page only), so the copy lives here and each page renders it.

export type UnfinishedSetupPress = 'end_session' | 'dismiss';

/** The badge shown in the invitation row's Status column. */
export function unfinishedSetupBadgeLabel(press: UnfinishedSetupPress): string {
  return press === 'end_session' ? 'Finishing setup' : 'Setup incomplete, session ended';
}

/** The action button's label, and its in-flight label. */
export function unfinishedSetupActionLabel(
  press: UnfinishedSetupPress,
  busy: boolean,
): string {
  if (press === 'end_session') return busy ? 'Ending session...' : 'End session';
  return busy ? 'Dismissing...' : 'Dismiss';
}

export interface UnfinishedSetupConfirmCopy {
  title: string;
  message: string;
  confirmLabel: string;
}

/**
 * The confirmation dialogue.
 *
 * Press 1 states plainly what ending the session costs: this invitation was
 * already accepted, so nothing is un-granted, but the invitee loses the only
 * route they had left to finish their own setup (the tab they still have open),
 * and their broker linkage and commission configuration may stay incomplete.
 * Press 2 is a reminder dismissal and says only that.
 */
export function unfinishedSetupConfirmCopy(
  email: string,
  press: UnfinishedSetupPress,
): UnfinishedSetupConfirmCopy {
  if (press === 'end_session') {
    return {
      title: 'End Setup Session',
      message:
        `${email} was already granted access, but their setup did not finish. ` +
        'Ending the setup session signs that session out, and it also ends their ' +
        'ability to finish the setup in the tab they still have open, so their broker ' +
        'linkage and commission settings may stay incomplete. It does NOT remove their ' +
        'access: remove them from Active Users if they should not have any, and send a ' +
        'fresh invitation if they still need to be set up.',
      confirmLabel: 'End Setup Session',
    };
  }
  return {
    title: 'Dismiss Setup Reminder',
    message:
      `The setup session for ${email} has already been ended. Dismissing removes this ` +
      'reminder only. Their access stays exactly as it is, and nothing else changes.',
    confirmLabel: 'Dismiss Reminder',
  };
}

/** The toast shown when the callable fails, per press. */
export function unfinishedSetupFailureLabel(press: UnfinishedSetupPress): string {
  return press === 'end_session'
    ? 'Failed to end the setup session: '
    : 'Failed to dismiss the setup reminder: ';
}
